import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import CoinLogo from "../commons/CoinLogo";
import { formatCrypto, formatCurrency } from "../../utils";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";

const ChartHeader = ({
  selectedCoin,
  setIsSelectPairModalOpen,
  desktopDropdownRef,
  setIsDesktopDropdownOpen,
  isDesktopDropdownOpen,
  searchTermDesktop,
  setSearchTermDesktop,
  filteredCoinsDesktop,
  handleSelectCoinDesktop,
}) => {
  // Pagination state for dropdown
  const [displayCount, setDisplayCount] = useState(10);
  const ITEMS_PER_PAGE = 10;

  // Track which coin rows are visible in viewport
  const [visibleIndices, setVisibleIndices] = useState(new Set());
  const itemRefs = useRef({});

  // Reset display count when dropdown opens or search changes
  useEffect(() => {
    if (isDesktopDropdownOpen) {
      setDisplayCount(10);
      setVisibleIndices(new Set());
      itemRefs.current = {};
    }
  }, [isDesktopDropdownOpen, searchTermDesktop]);

  // Get paginated coins
  const paginatedCoins = useMemo(() => {
    return filteredCoinsDesktop.slice(0, displayCount);
  }, [filteredCoinsDesktop, displayCount]);

  // Check if there are more coins to load
  const hasMore = displayCount < filteredCoinsDesktop.length;

  // Intersection Observer to track visible rows
  useEffect(() => {
    if (!isDesktopDropdownOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute("data-index"));
          if (!isNaN(index)) {
            setVisibleIndices((prev) => {
              const newSet = new Set(prev);
              if (entry.isIntersecting) {
                newSet.add(index);
              } else {
                // Keep items visible even after they leave viewport
                // This prevents unnecessary re-fetching
              }
              return newSet;
            });
          }
        });
      },
      {
        root: document.querySelector("[data-dropdown-list]"),
        rootMargin: "100px", // Load 100px before entering viewport
        threshold: 0.01,
      }
    );

    // Observe all items
    Object.values(itemRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [isDesktopDropdownOpen, paginatedCoins.length]);

  // Handle scroll to load more
  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // When user scrolls to 80%, load more items
    if (scrollPercentage > 0.8 && hasMore) {
      setDisplayCount((prev) =>
        Math.min(prev + ITEMS_PER_PAGE, filteredCoinsDesktop.length)
      );
    }
  };

  // Ref callback to store item refs
  const setItemRef = useCallback((element, index) => {
    if (element) {
      itemRefs.current[index] = element;
    }
  }, []);

  return (
    <div className="mx-auto bg-white pb-2">
      <div className="flex flex-row">
        <div className="flex flex-col md:flex-row w-[40%]">
          <div className="flex space-x-3 w-[60%]">
            <CoinLogo
              asset={selectedCoin?.symbol}
              className="w-6 h-6 rounded-full mt-2"
            />
            <div
              onClick={() => setIsSelectPairModalOpen(true)}
              className="flex items-center space-x-2 text-left cursor-pointer xl:hidden"
            >
              <div>
                <div className="text-[16px] md:text-base font-bold text-gray-800 !uppercase">
                  {selectedCoin?.symbol}
                </div>
                <div className="flex items-center space-x-1">
                  <span className="text-[16px] md:text-sm text-gray-500">
                    {selectedCoin?.name}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-4 h-4 text-gray-400"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-700"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            <div className="relative hidden xl:block" ref={desktopDropdownRef}>
              <div
                onClick={() => setIsDesktopDropdownOpen(!isDesktopDropdownOpen)}
                className="flex items-center space-x-2 text-left cursor-pointer"
              >
                <div>
                  <div className="text-[16px] md:text-base font-bold text-gray-800 uppercase">
                    {selectedCoin?.symbol}/USDT
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-[16px] md:text-sm text-gray-500">
                      {selectedCoin?.name}
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4 text-gray-400"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1 a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-700"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {isDesktopDropdownOpen && (
                <div className="absolute left-[-20px] mt-2 w-[450px] bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="p-3 pb-1">
                    <div className="relative p-1">
                      <input
                        type="text"
                        placeholder="Search Coin Name"
                        className="w-full p-3 py-2 pl-9 bg-[#F2F4F8] border-none rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        value={searchTermDesktop}
                        onChange={(e) => setSearchTermDesktop(e.target.value)}
                      />
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div
                    className="p-2 max-h-60 overflow-y-auto"
                    onScroll={handleDropdownScroll}
                    data-dropdown-list
                  >
                    <div className="flex justify-between items-center text-xs md:text-sm text-gray-500 mb-2 px-2">
                      <span className="flex-1">Pair</span>
                      <span className="w-20 text-right">Price</span>
                      <span className="w-16 text-right">24H</span>
                    </div>
                    {paginatedCoins.length > 0 ? (
                      <>
                        {paginatedCoins.map((coin, index) => {
                          const isVisible = visibleIndices.has(index);
                          const isSelected = selectedCoin?.id === coin?.id;

                          return (
                            <a
                              href="#"
                              key={coin.id}
                              data-index={index}
                              ref={(el) => setItemRef(el, index)}
                              onClick={(e) => {
                                e.preventDefault();
                                handleSelectCoinDesktop(coin);
                              }}
                              className={`flex items-center gap-3 relative px-2 py-3 pl-3 ml-[-.5rem] text-xs md:text-sm text-gray-700 w-[calc(100%+.5rem)]
                                    ${
                                      isSelected
                                        ? "bg-[#F0F0FF] text-black font-medium after:absolute after:h-full after:w-[5px] after:bg-[#2D224E] after:left-0"
                                        : "hover:bg-gray-100"
                                    }
                                    `}
                            >
                              {/* Only render CoinLogo if visible or selected */}
                              <CoinLogo
                                asset={coin?.symbol}
                                className="w-6 h-6 rounded-full"
                              />
                              <span className="flex-1 font-medium uppercase">
                                {coin?.symbol}
                              </span>
                              <span className="w-20 text-right">
                                {formatCrypto(coin?.current_price)}
                              </span>
                              <span
                                className={`w-16 text-right ${
                                  coin?.price_change_percentage_24h >= 0
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {formatCrypto(
                                  coin?.price_change_percentage_24h
                                )}
                                %
                              </span>
                            </a>
                          );
                        })}
                        {hasMore && (
                          <div className="text-center text-xs text-gray-400 py-2">
                            Scroll for more... ({displayCount}/
                            {filteredCoinsDesktop.length})
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center text-sm text-gray-500 py-4">
                        No results found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="text-left w-[40%]">
            <div
              className={`text-[14px] md:text-base font-bold ${
                selectedCoin?.price_change_percentage_24h >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              ${formatCrypto(selectedCoin?.current_price)}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row w-[30%] items-end md:items-start gap-2 md:gap-0">
          <div className="text-right md:text-left w-1/2">
            <div className="text-sm md:text-base text-gray-500 whitespace-nowrap">
              24H Change
            </div>
            <div>
              <div
                className={`text-sm md:text-base font-semibold leading-tight ${
                  selectedCoin?.price_change_percentage_24h >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {formatCrypto(selectedCoin?.price_change_24h)}
              </div>
              <div
                className={`text-xs md:text-sm font-semibold leading-tight ${
                  selectedCoin?.price_change_percentage_24h >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                ({selectedCoin?.price_change_percentage_24h >= 0 ? "+" : ""}
                {formatCrypto(selectedCoin?.price_change_percentage_24h)}%)
              </div>
            </div>
          </div>

          <div className="text-right md:text-left w-1/2">
            <div className="text-[12px] md:text-base text-gray-500 whitespace-nowrap">
              24H High
            </div>
            <div className="text-[12px] md:text-base font-semibold text-gray-800">
              ${formatCrypto(selectedCoin?.high_24h)}
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row w-[30%] items-end md:items-start gap-4 md:gap-0">
          <div className="text-right md:text-left w-1/2">
            <div className="text-[12px] md:text-base text-gray-500 whitespace-nowrap">
              24H Low
            </div>
            <div className="text-[12px] md:text-base font-semibold text-gray-800">
              ${formatCrypto(selectedCoin?.low_24h)}
            </div>
          </div>
          <div className="text-right md:text-left w-1/2">
            <>
              <div className="block text-[12px] md:text-base text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis w-full max-w-[140px]">
                24H Vol (USDT)
              </div>
            </>
            <div className="text-[12px] md:text-base font-semibold text-gray-800 w-full max-w-[140px] overflow-hidden whitespace-nowrap text-ellipsis">
              {formatCurrency(selectedCoin?.total_volume / 1_000_000)}M
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartHeader;
